import { AssetReadyForSubmission } from "@/api/submission-types";
import { AssetsForSubmissionSummary } from "@/components/submission/AssetsForSubmissionSummary";
import { SubmissionFilters } from "@/components/submission/SubmissionFilters";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { Link } from "@/components/ui/Link";
import { Loader } from "@/components/ui/loaders";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { useAssetsReadyForSubmission } from "@/queries/submission/use-assets-ready-for-submission";
import { useSubmitAssets } from "@/queries/submission/use-submit-assets";
import { genderOptions, storyTypeOptions } from "@/utils/datagrid-utils";
import { Button, Stack } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFilterItem,
  GridSortModel,
} from "@mui/x-data-grid";
import { useState } from "react";

const columns: GridColDef<AssetReadyForSubmission>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 120,
    filterable: false,
    renderCell: (params) => (
      <Link to={`/episodes/${params.row.id}`}>{params.row.id}</Link>
    ),
  },
  {
    field: "story",
    headerName: "Story Type",
    width: 120,
    type: "singleSelect",
    valueOptions: storyTypeOptions,
    filterable: false,
  },
  {
    field: "category",
    headerName: "Category",
    width: 120,
    filterable: false,
  },
  {
    field: "subcategory",
    headerName: "Subcategory",
    width: 120,
    sortable: false,
    filterable: false,
  },
  {
    field: "gender",
    headerName: "Gender",
    width: 90,
    filterable: false,
    type: "singleSelect",
    valueOptions: genderOptions,
  },
  {
    field: "imagesForSubmissionSummary",
    headerName: "Images",
    width: 120,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <AssetsForSubmissionSummary
        summary={params.row.imagesForSubmissionSummary}
      />
    ),
  },
  {
    field: "masksForSubmissionSummary",
    headerName: "Masks",
    width: 120,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <AssetsForSubmissionSummary
        summary={params.row.masksForSubmissionSummary}
      />
    ),
  },
];

export function SubmissionPage() {
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterItems, setFilterItems] = useState<GridFilterItem[]>([]);

  const { data, isLoading, error } = useAssetsReadyForSubmission({
    sortModel,
    filterModel: { items: filterItems },
  });

  const {
    mutate: submitAssets,
    error: submitAssetsError,
    isPending: isSubmitAssetsPending,
  } = useSubmitAssets();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  return (
    <Stack gap={2} mb={2}>
      <SubmissionFilters items={filterItems} onChange={setFilterItems} />
      <ApiErrorAlert error={error} />
      <ApiErrorSnackbar error={submitAssetsError} />
      <DataGrid
        rowHeight={46}
        columnHeaderHeight={46}
        keepNonExistentRowsSelected
        checkboxSelection
        columns={columns}
        loading={isLoading}
        rows={data ?? []}
        rowSelectionModel={selectedIds}
        sortingMode="server"
        sortModel={sortModel}
        onRowSelectionModelChange={(model) => {
          setSelectedIds(model.map(String));
        }}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 25, 50, 100]}
      />
      <Actions
        isSubmitPending={isSubmitAssetsPending}
        onSubmit={() => {
          if (isSubmitAssetsPending) {
            return;
          }
          submitAssets(
            { episodeIds: selectedIds },
            {
              onSuccess: () => {
                setSelectedIds([]);
              },
            },
          );
        }}
      />
    </Stack>
  );
}

function Actions({
  isSubmitPending,
  onSubmit,
}: {
  isSubmitPending: boolean;
  onSubmit: () => void;
}) {
  return (
    <Stack
      direction={"row"}
      gap={1}
      justifyContent={"flex-end"}
      alignItems={"center"}
    >
      <Loader isLoading={isSubmitPending} />
      <Button variant="contained" disabled={isSubmitPending} onClick={onSubmit}>
        Submit
      </Button>
    </Stack>
  );
}
